import { styled } from '@/styled-system/jsx';

export const ModalSizes = {
  xs: '360px',
  sm: '540px',
  md: '720px',
  lg: '900px',
  xl: '1080px',
  xxl: '1240px',
  // Numeric sizes for specific pixel values
  '320': '320px',
  '420': '420px',
  '480': '480px',
  '640': '640px',
  '800': '800px',
};

export const PopupModalOverlay = styled('aside', {
  base: {
    position: 'fixed',
    top: 0,
    right: 0,
    left: 0,
    bottom: 0,
    zIndex: 'zIndexOverLoader',
    background: 'hpModalOverlay',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // Prevent viewport height issues on mobile
    height: '100vh',
    '@supports (height: 100dvh)': {
      height: '100dvh',
    },
  },
  variants: {
    darkOverlay: {
      true: {
        background: 'hpGrayTranslucent',
      },
    },
    transparentOverlay: {
      true: {
        background: 'transparent',
      },
    },
    position: {
      top: {
        md: {
          alignItems: 'flex-start',
          paddingTop: '{spacing.3x}',
          paddingBottom: '{spacing.2x}',
        },
      },
    },
  },
});

export const PopupModalContent = styled('div', {
  base: {
    background: 'hpxWhite',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    // Use dvh unit for dynamic viewport height on mobile
    '@supports (height: 100dvh)': {
      height: '100dvh',
    },
    // Prevent layout shifts
    contain: 'layout style',
    // Mobile: full height, scroll when content overflows
    overflowX: 'hidden',
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    WebkitOverflowScrolling: 'touch',
    /* Custom scrollbar styling */
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'hpxGrey100',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'hpxGrey500',
      borderRadius: '4px',
      '&:hover': {
        background: 'hpxGrey600',
      },
    },
    // Firefox support
    scrollbarWidth: 'thin',
    scrollbarColor: 'hpxGrey500 hpxGrey100',

    md: {
      position: 'relative',
      borderRadius: '{spacing.1x}',
      // Desktop: grow to fit content, scroll only when hitting maxHeight
      height: 'auto',
      width: 'calc(100% - 32px)', // Fixed width with 16px margins on each side
      maxWidth: 'calc(100% - 32px)',
      maxHeight: 'calc(100vh - 32px)',
      boxShadow: '0 4px 30px 0 rgba(0, 0, 0, 0.15)',
    },
  },
  variants: {
    size: {
      auto: {
        md: { width: 'auto' },
      },
      xs: {
        md: { maxWidth: ModalSizes.xs },
      },
      sm: {
        md: { maxWidth: ModalSizes.sm },
      },
      md: {
        md: { maxWidth: ModalSizes.md },
      },
      lg: {
        md: { maxWidth: ModalSizes.lg },
      },
      xl: {
        md: { maxWidth: ModalSizes.xl },
      },
      xxl: {
        md: { maxWidth: ModalSizes.xxl },
      },
      '320': {
        md: { maxWidth: '320px' },
      },
      '420': {
        md: { maxWidth: '420px' },
      },
      '480': {
        md: { maxWidth: '480px' },
      },
      '640': {
        md: { maxWidth: '640px' },
      },
      '800': {
        md: { maxWidth: '800px' },
      },
    },
    slideUp: {
      true: {
        top: 'calc(100vh - 340px)',
        left: '2.5%',
        width: '95%',
        position: 'fixed',
        boxShadow: '0 1px 30px 0 rgba(0, 0, 0, 0.15)',
        animation: 'fadingIn',

        md: {
          animation: 'none',
          top: '50%',
          left: '50%',
          width: '350px',
        },
      },
    },
    fullHeight: {
      true: {
        md: { height: '100%' },
      },
    },
    isFullScreen: {
      true: {
        md: {
          borderRadius: 0,
          width: '100vw',
          height: '100vh',
        },
      },
    },
    position: {
      top: {
        md: {
          maxHeight: 'calc(100vh - {spacing.6x})', // Account for overlay padding
        },
      },
    },
    floatingCloseButton: {
      true: {},
    },
  },
  defaultVariants: {
    size: 'auto',
  },
});

export const PopupModalCloseButton = styled('button', {
  base: {
    marginTop: '0px',
    marginRight: '0px',
    padding: '8px',
    position: 'absolute',
    top: '8px',
    right: '8px',
    cursor: 'pointer',
    borderRadius: '4px',
    zIndex: 2,
    '&:hover': {
      backgroundColor: 'hpxGrey100',
    },
    '&:focus': {
      outlineOffset: '2px',
      outlineWidth: '2px',
      outlineStyle: 'solid',
      outlineColor: 'hpxTeal500',
    },
  },
  variants: {
    isFullScreen: {
      true: {
        top: '8px',
        right: '8px',
        md: {
          top: '16px',
          right: '16px',
        },
      },
    },
    floatingCloseButton: {
      true: {
        position: 'absolute',
        right: '-48px',
        zIndex: 'zIndexMax',
      },
    },
    scrollablePaddingInline: {
      xs: { right: 'xs' },
      '1x': { right: '1x' },
      '2x': { right: '2x' },
      '3x': { right: '3x' },
      '4x': { right: '4x' },
      '6x': { right: '6x' },
      '8x': { right: '8x' },
    },
  },
});

export const ScrollableContent = styled('section', {
  base: {
    // Simple content wrapper - scrolling is handled by PopupModalContent
    width: '100%',
  },
  variants: {
    paddingTop: {
      '0': { paddingTop: 0 },
      xs: { paddingTop: 'xs' },
      '1x': { paddingTop: '1x' },
      '2x': { paddingTop: '2x' },
      '3x': { paddingTop: '3x' },
      '4x': { paddingTop: '4x' },
      '6x': { paddingTop: '6x' },
      '8x': { paddingTop: '8x' },
    },
    paddingBottom: {
      '0': { paddingBottom: 0 },
      xs: { paddingBottom: 'xs' },
      '1x': { paddingBottom: '1x' },
      '2x': { paddingBottom: '2x' },
      '3x': { paddingBottom: '3x' },
      '4x': { paddingBottom: '4x' },
      '6x': { paddingBottom: '6x' },
      '8x': { paddingBottom: '8x' },
    },
    paddingInline: {
      xs: { paddingLeft: 'xs', paddingRight: 'xs' },
      '1x': { paddingLeft: '1x', paddingRight: '1x' },
      '2x': { paddingLeft: '2x', paddingRight: '2x' },
      '3x': { paddingLeft: '3x', paddingRight: '3x' },
      '4x': { paddingLeft: '4x', paddingRight: '4x' },
      '6x': { paddingLeft: '6x', paddingRight: '6x' },
      '8x': { paddingLeft: '8x', paddingRight: '8x' },
    },
  },
  defaultVariants: {},
});
